import React, { useCallback } from 'react';
import styled from 'styled-components';
import PetForm from './PetForm';

const PetsContainer = styled.div`
  margin-top: 20px;
`;

const PetItem = styled.div`
  background: ${props => props.theme.colors.grayLight};
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
  position: relative;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const PetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  flex-wrap: wrap;
  gap: 10px;
`;

const PetTitle = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const RemoveButton = styled.button`
  background: ${props => props.theme.colors.error};
  color: ${props => props.theme.colors.white};
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: background-color 0.2s ease;

  &:hover {
    background: #dc2626;
  }
`;

const AddButton = styled.button`
  background: ${props => props.theme.colors.secondary};
  color: ${props => props.theme.colors.white};
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: background-color 0.2s ease;
  text-align: center;
  display: block;
  margin: 0 auto;

  &:hover {
    background: ${props => props.theme.colors.primary};
  }
`;

const PetsGrid = ({ pets = [], onPetsChange, selectedPlan, error }) => {
  // Usar timestamp + random para IDs únicos e evitar conflitos
  const generateUniqueId = () => {
    // Usar apenas timestamp + contador simples para garantir unicidade
    return `pet-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
  };

  const addPet = useCallback(() => {
    const newPetId = generateUniqueId();
    const newPet = {
      id: newPetId,
      name: '',
      age: '',
      weight: '',
      breed: '',
      sex: '',
      service: '',
      group: '',
      size: ''
    };
    
    const updatedPets = [...pets, newPet];
    onPetsChange(updatedPets);
  }, [pets, onPetsChange]);

  const removePet = useCallback((id) => {
    if (pets.length === 1) {
      pets = [];
      onPetsChange(pets);
      return;
    } else if (pets.length === 0) {
      return;
    } else {
      const updatedPets = pets.filter(pet => pet.id !== id);
      onPetsChange(updatedPets);
    }
  }, [pets, onPetsChange]);

  const updatePet = useCallback((id, data) => {
    const updatedPets = pets.map(pet => (
      pet.id === id ? { ...pet, ...data } : pet
    ));
    onPetsChange(updatedPets);
  }, [pets, onPetsChange]);

  const getPetTitle = (pet) => {
    if (pet.name && pet.name.trim()) {
      return pet.name.trim();
    }
    // Encontrar o índice do pet no array para mostrar "PET 1", "PET 2", etc.
    const index = pets.findIndex(p => p.id === pet.id);
    return `PET ${index + 1}`;
  };

  return (
    <PetsContainer>
      {pets.map((pet) => (
        <PetItem key={pet.id}>
          <PetHeader>
            <PetTitle>
              {getPetTitle(pet)}
            </PetTitle>
            <RemoveButton onClick={() => {
              removePet(pet.id);
            }}>
              Remover
            </RemoveButton>
          </PetHeader>
          
          <PetForm
            petId={pet.id}
            onDataChange={(data) => updatePet(pet.id, data)}
            data={pet}
            error={error}
            selectedPlan={selectedPlan}
          />
        </PetItem>
      ))}

      <AddButton type="button" onClick={addPet}>
        + Adicionar Pet
      </AddButton>
    </PetsContainer>
  );
};

export default PetsGrid;
